import { FC, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { IOrder, EOrderStatus, IOrdersListFilters } from "../types";
import { ColumnType } from "antd/lib/table";
import { actions } from "../slice";
import { AppDispatch } from "../../../app/store";
import { ApiStatuses, SortTypes } from "../../../app/types";
import { LMTable } from "../../../components/LMTable";
import { OrderStatus } from "../OrderStatus";
import { useLink } from "../../../components/LMLink";
import { LMNewButton } from "../../../components/LMNewButton";
import { IconExport } from "../../../assets";
import { useAppNotifications } from "../../../components/LMNotifications";
import styles from "./OrdersList.module.css";

interface Props {
  orders: IOrder[];
  selectedOrdersToProcess: IOrder[];
  filters: IOrdersListFilters;
  status: ApiStatuses;
  onExport: () => void;
}

export const OrdersList: FC<Props> = ({
  orders,
  selectedOrdersToProcess,
  filters,
  onExport,
  status,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useLink();
  const { contextHolder } = useAppNotifications();
  const { t } = useTranslation("orders");

  const handlePageChange = (updatedPage: number) => {
    dispatch(actions.setPage(updatedPage - 1));
  };

  const handlePageSizeChange = (updatedSize: string) => {
    dispatch(actions.setPageSize(Number(updatedSize)));
    dispatch(actions.setPage(0));
  };

  const handleSortDateChange = (column: string, order: SortTypes) => {
    dispatch(actions.setDateSort(order));
  };

  const columns = useMemo(
    () => [
      {
        title: t("id"),
        dataIndex: "id",
        key: "id",
        //fixed: "left",
        //width: 120,
      },
      {
        title: t("time"),
        key: "created",
        render: (order: IOrder) => {
          return `${order.deliveryDate} ${order.deliveryTime}`;
        },
      },
      {
        title: t("source"),
        key: "source",
        dataIndex: "internal",
        render: (internal: boolean) => (internal ? t("user") : t("api")),
      },
      {
        title: t("status"),
        dataIndex: "status",
        key: "status",
        render: (status: EOrderStatus) => <OrderStatus status={status} />,
      },
      {
        title: t("displayCode"),
        dataIndex: "displayCode",
        key: "displayCode",
      },
      {
        title: t("priority"),
        dataIndex: "priority",
        key: "priority",
      },
    ],
    [t]
  ) as ColumnType<IOrder>[];

  const handleRowClick = (order: IOrder) => navigate(`/orders/${order.id}`);

  const onRow = (media: IOrder) => ({
    onClick: () => handleRowClick(media),
    style: { cursor: "pointer" },
  });

  const getTotalTitle = () => {
    if (selectedOrdersToProcess.length === 1) {
      return t("selectedOrder");
    }
    if (selectedOrdersToProcess.length > 0) {
      return t("selectedOrders", { amount: selectedOrdersToProcess.length });
    }
    return t("total");
  };

  return (
    <>
      {contextHolder}
      <LMTable
        className={styles.table}
        loading={status === ApiStatuses.loading}
        columns={columns}
        dataSource={orders}
        total={{
          title: getTotalTitle(),
          amount: selectedOrdersToProcess.length
            ? undefined
            : filters.totalItems,
        }}
        onChangePageSize={handlePageSizeChange}
        onChangePage={handlePageChange}
        onSortChange={handleSortDateChange}
        pageSize={filters.pageSize}
        page={filters.page + 1}
        onRow={onRow}
        rowKey="id"
      />
    </>
  );
};
